import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  BarChart3,
  BookOpen,
  Building2,
  ClipboardCheck,
  FileText,
  GraduationCap,
  LayoutDashboard,
  Library,
  LineChart,
  LogOut,
  Moon,
  PencilRuler,
  Sun,
  User,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { hasRole } from "@/components/app/roles";
import { clearSession, getUser } from "@/auth.js";
import { api } from "@/api.js";

// Global Cmd/Ctrl+K command palette. Mounted once in AppLayout so every
// authenticated page can launch it. TestRunner uses FullscreenLayout and
// therefore doesn't mount this — good; students shouldn't be jumping around
// mid-exam.
type Hit = {
  kind: "tenant" | "user" | "assessment" | "member";
  id: string;
  label: string;
  sublabel: string | null;
  to: string;
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [searching, setSearching] = useState(false);
  const nav = useNavigate();
  const { setTheme, resolvedTheme } = useTheme();
  const user = getUser();
  const role = user?.role ?? "student";
  const isSuperAdmin = hasRole(role, "super_admin");
  // Matches Sidebar + ExaminerRoute — tenant admins and super admins
  // don't auto-qualify as examiners.
  const isExaminer = !!user?.is_examiner || role === "moderator";

  // Debounced cross-entity search. 250ms is short enough to feel live,
  // long enough to coalesce typing. Super-admins hit /platform/search
  // (tenants + users); everyone else hits the tenant-scoped endpoint.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      return;
    }
    const handle = window.setTimeout(async () => {
      setSearching(true);
      try {
        const res = isSuperAdmin
          ? await api.platformSearch(q)
          : await api.tenantSearch(q);
        setHits(res.hits);
      } catch {
        setHits([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [query, isSuperAdmin]);

  const grouped = useMemo(() => {
    const g: Record<Hit["kind"], Hit[]> = {
      tenant: [],
      user: [],
      assessment: [],
      member: [],
    };
    for (const h of hits) g[h.kind].push(h);
    return g;
  }, [hits]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    // TopBar's search button dispatches this custom event instead of
    // faking a keyboard shortcut — cleaner than synthesising KeyboardEvent.
    const openPalette = () => setOpen(true);
    document.addEventListener("keydown", down);
    window.addEventListener("exambase:open-search", openPalette);
    return () => {
      document.removeEventListener("keydown", down);
      window.removeEventListener("exambase:open-search", openPalette);
    };
  }, []);

  function go(to: string) {
    setOpen(false);
    nav(to);
  }

  async function signOut() {
    setOpen(false);
    try {
      await api.logout();
    } catch {
      // ignore — the local clear below still runs so the user gets out
    }
    clearSession();
    nav("/login", { replace: true });
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setQuery("");
          setHits([]);
        }
      }}
    >
      <CommandInput
        placeholder={
          isSuperAdmin
            ? "Search tenants, users, or type a command…"
            : "Search assessments, members, or type a command…"
        }
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[60vh]">
        <CommandEmpty>
          {searching ? (
            <span className="inline-flex items-center gap-2">
              <span className="h-2 w-2 animate-pulse rounded-full bg-primary" />
              Searching…
            </span>
          ) : query.trim().length === 0 ? (
            <div className="space-y-3 py-6 text-left">
              <p className="text-sm text-muted-foreground">
                Type at least 2 characters to search, or pick a shortcut below.
              </p>
              <div className="flex flex-wrap gap-1.5 text-xs">
                <span className="rounded border bg-muted/60 px-1.5 py-0.5 font-mono">
                  ↑↓
                </span>
                <span className="text-muted-foreground">navigate</span>
                <span className="ml-2 rounded border bg-muted/60 px-1.5 py-0.5 font-mono">
                  ↵
                </span>
                <span className="text-muted-foreground">select</span>
                <span className="ml-2 rounded border bg-muted/60 px-1.5 py-0.5 font-mono">
                  esc
                </span>
                <span className="text-muted-foreground">close</span>
              </div>
            </div>
          ) : query.trim().length < 2 ? (
            <span className="text-sm text-muted-foreground">
              Keep typing…
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">
              No matches for <em>"{query.trim()}"</em>.
            </span>
          )}
        </CommandEmpty>
        {grouped.tenant.length > 0 && (
          <CommandGroup heading="Tenants">
            {grouped.tenant.map((h) => (
              <CommandItem key={h.id} onSelect={() => go(h.to)}>
                <Building2 className="mr-2 h-4 w-4" />
                <span className="flex-1">
                  {h.label}
                  {h.sublabel && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {h.sublabel}
                    </span>
                  )}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {grouped.user.length > 0 && (
          <CommandGroup heading="Users">
            {grouped.user.map((h) => (
              <CommandItem key={h.id} onSelect={() => go(h.to)}>
                <User className="mr-2 h-4 w-4" />
                <span className="flex-1">
                  {h.label}
                  {h.sublabel && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {h.sublabel}
                    </span>
                  )}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {grouped.assessment.length > 0 && (
          <CommandGroup heading="Assessments">
            {grouped.assessment.map((h) => (
              <CommandItem key={h.id} onSelect={() => go(h.to)}>
                <FileText className="mr-2 h-4 w-4" />
                <span className="flex-1">
                  {h.label}
                  {h.sublabel && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {h.sublabel}
                    </span>
                  )}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {grouped.member.length > 0 && (
          <CommandGroup heading="Members">
            {grouped.member.map((h) => (
              <CommandItem key={h.id} onSelect={() => go(h.to)}>
                <User className="mr-2 h-4 w-4" />
                <span className="flex-1">
                  {h.label}
                  {h.sublabel && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {h.sublabel}
                    </span>
                  )}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {hits.length > 0 && <CommandSeparator />}
        <CommandGroup heading="Navigate">
          <CommandItem onSelect={() => go("/dashboard")}>
            <LayoutDashboard className="mr-2 h-4 w-4" />
            Assessments
          </CommandItem>
          {role === "student" && (
            <CommandItem onSelect={() => go("/progress")}>
              <LineChart className="mr-2 h-4 w-4" />
              My progress
            </CommandItem>
          )}
          {isExaminer && (
            <CommandItem onSelect={() => go("/marking")}>
              <ClipboardCheck className="mr-2 h-4 w-4" />
              Marking queue
            </CommandItem>
          )}
          {hasRole(role, "teacher") && (
            <>
              <CommandItem onSelect={() => go("/authoring")}>
                <PencilRuler className="mr-2 h-4 w-4" />
                Authoring
              </CommandItem>
              <CommandItem onSelect={() => go("/stats")}>
                <BarChart3 className="mr-2 h-4 w-4" />
                Stats
              </CommandItem>
            </>
          )}
          {hasRole(role, "super_admin") && (
            <>
              <CommandItem onSelect={() => go("/platform")}>
                <Building2 className="mr-2 h-4 w-4" />
                Platform tenants
              </CommandItem>
              <CommandItem onSelect={() => go("/platform/metrics")}>
                <GraduationCap className="mr-2 h-4 w-4" />
                Platform metrics
              </CommandItem>
              <CommandItem onSelect={() => go("/platform/questions")}>
                <Library className="mr-2 h-4 w-4" />
                Question bank
              </CommandItem>
            </>
          )}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Preferences">
          <CommandItem
            onSelect={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
          >
            {resolvedTheme === "dark" ? (
              <Sun className="mr-2 h-4 w-4" />
            ) : (
              <Moon className="mr-2 h-4 w-4" />
            )}
            Toggle theme
          </CommandItem>
          <CommandItem onSelect={() => go("/dashboard")}>
            <BookOpen className="mr-2 h-4 w-4" />
            Home
          </CommandItem>
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Account">
          <CommandItem onSelect={signOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
            <CommandShortcut>⌘⇧Q</CommandShortcut>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
